import { useState } from "react";
import useReveal from "../hooks/useReveal";
import Navbar from "../components/Navbar/Navbar";
import Newsletter from "../components/Newsletter/Newsletter";
import Footer from "../components/Footer/Footer";
import "./Careers.css";

const PERKS = [
  { icon: "💰", title: "Competitive Salary",  desc: "Industry-leading pay with performance bonuses." },
  { icon: "🌍", title: "Remote Friendly",     desc: "Flexible work-from-home across all roles." },
  { icon: "📚", title: "Learning Budget",     desc: "₹50,000/year for courses, books & conferences." },
  { icon: "🏥", title: "Health Insurance",    desc: "Full medical, dental and vision coverage." },
  { icon: "🎮", title: "Game Library",        desc: "Free access to all iGamesTech titles." },
  { icon: "⏰", title: "Flexible Hours",      desc: "We care about output, not clock time." },
];

const ROLES = [
  "Game Developer", "UI/UX Designer", "Frontend Developer",
  "Backend Engineer", "3D Artist", "Game Designer", "Other",
];

const EMPTY = { name: "", email: "", phone: "", role: "", portfolio: "", experience: "", message: "", resume: null };

export default function Careers() {
  useReveal();
  const [form, setForm]           = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError]         = useState("");

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const onFileChange = (e) => setForm({ ...form, resume: e.target.files[0] });

  const onSubmit = async (e) => {
    e.preventDefault(); setError(""); setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('jobTitle', form.role);
      formData.append('name', form.name);
      formData.append('email', form.email);
      formData.append('phone', form.phone);
      formData.append('portfolio', form.portfolio);
      formData.append('experience', form.experience);
      formData.append('message', form.message);
      if (form.resume) formData.append('resume', form.resume);

      const res = await fetch(`${process.env.REACT_APP_API_URL}/applications`, {
        method: "POST",
        body: formData,
      });
      if (res.ok) { setSubmitted(true); setForm(EMPTY); }
      else { const d = await res.json(); setError(d.message || "Submission failed. Try again."); }
    } catch { setSubmitted(true); setForm(EMPTY); }
    finally { setSubmitting(false); }
  };

  return (
    <>
      <Navbar />

      {/* ── Hero ── */}
      <section className="car-hero">
        <div className="car-hero-inner reveal">
          <span className="car-badge">We're Hiring</span>
          <h1>Build the future of<br />gaming with us</h1>
          <p>Join a passionate team of game developers, designers and engineers creating next-generation gaming experiences at iGamesTech.</p>
          <a href="#apply" className="btn-primary">Apply Now</a>
        </div>
      </section>

      {/* ── Perks ── */}
      <section className="car-perks section">
        <div className="container">
          <div className="reveal" style={{ textAlign: "center", marginBottom: "48px" }}>
            <h2 className="section-title">Why work at iGamesTech?</h2>
            <p className="section-desc" style={{ margin: "0 auto" }}>We invest in our people as much as we invest in our games.</p>
          </div>
          <div className="car-perks-grid">
            {PERKS.map((p, i) => (
              <div className="car-perk-card reveal" key={i} style={{ transitionDelay: `${i * 0.07}s` }}>
                <div className="car-perk-icon">{p.icon}</div>
                <h3>{p.title}</h3>
                <p>{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Application Form ── */}
      <section className="car-apply section" id="apply">
        <div className="container">
          <div className="car-apply-inner">

            {/* Left: info */}
            <div className="car-apply-info reveal">
              <span className="section-label">Join the Team</span>
              <h2 className="section-title">Ready to join us?</h2>
              <p className="section-desc">Fill in the form and tell us about yourself. We review every application personally and get back to you within 5 business days.</p>

              <div className="car-contact-list">
                <div className="car-contact-item">
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.8 19.79 19.79 0 01.1 1.18 2 2 0 012.1.01h3a2 2 0 012 1.72c.13.96.36 1.9.7 2.81a2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0122 16.92z"/>
                  </svg>
                  <span>+91 92113 43912</span>
                </div>
                <div className="car-contact-item">
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/>
                  </svg>
                  <span>careers@igamestech.com</span>
                </div>
                <div className="car-contact-item">
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/>
                  </svg>
                  <span>Unit No. 1017, 10th Floor, SS Omnia,<br/>Sector 88, Gurugram</span>
                </div>
              </div>
            </div>

            {/* Right: form */}
            <div className="car-apply-form-wrap reveal">
              {submitted ? (
                <div className="car-success">
                  <span>🎉</span>
                  <h3>Application Received!</h3>
                  <p>Thank you for your interest in iGamesTech. We'll review your application and get back to you within 5 business days.</p>
                  <button className="btn-primary" onClick={() => { setSubmitted(false); setForm(EMPTY); }}>Submit Another</button>
                </div>
              ) : (
                <form className="car-form" onSubmit={onSubmit}>
                  <div className="car-form-row">
                    <div className="car-field">
                      <label>Full Name *</label>
                      <input type="text"  name="name"  placeholder="Your full name"  value={form.name}  onChange={onChange} required/>
                    </div>
                    <div className="car-field">
                      <label>Email *</label>
                      <input type="email" name="email" placeholder="your@email.com"  value={form.email} onChange={onChange} required/>
                    </div>
                  </div>
                  <div className="car-form-row">
                    <div className="car-field">
                      <label>Phone</label>
                      <input type="text"  name="phone" placeholder="+91 00000 00000" value={form.phone} onChange={onChange}/>
                    </div>
                    <div className="car-field">
                      <label>Role You're Applying For *</label>
                      <select name="role" value={form.role} onChange={onChange} required>
                        <option value="" disabled>Select a role</option>
                        {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="car-form-row">
                    <div className="car-field">
                      <label>Portfolio / LinkedIn</label>
                      <input type="text" name="portfolio" placeholder="https://..." value={form.portfolio} onChange={onChange}/>
                    </div>
                    <div className="car-field">
                      <label>Upload Resume *</label>
                      <input type="file" name="resume" accept=".pdf,.doc,.docx" onChange={onFileChange} required/>
                    </div>
                  </div>
                  <div className="car-form-row">
                    <div className="car-field">
                      <label>Years of Experience</label>
                      <input type="text" name="experience" placeholder="e.g. 3 years" value={form.experience} onChange={onChange}/>
                    </div>
                  </div>
                  <div className="car-field">
                    <label>Tell Us About Yourself *</label>
                    <textarea name="message" placeholder="Share your background, skills and why you'd love to work at iGamesTech..." rows="5" value={form.message} onChange={onChange} required/>
                  </div>
                  {error && <p className="car-error">{error}</p>}
                  <button type="submit" className="btn-primary car-submit" disabled={submitting}>
                    {submitting ? "Submitting..." : "Submit Application"}
                  </button>
                </form>
              )}
            </div>

          </div>
        </div>
      </section>

      <Newsletter />
      <Footer />
    </>
  );
}
